import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { CancelButton } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogDataContextEventsTransformedSelector } from 'src/contexts/CatalogDataContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { Button, Stack } from 'src/core/ui';
import { useEventItemLoadingDisplay } from 'src/hooks/useEventItemLoadingDisplay';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  ActionOutboxEntityType,
  EventWithData,
  ListingDetails,
} from 'src/WebApiController';

import { CancellableFormHeader } from '../common/CancellableFormHeader';
import { HeaderContent } from '../common/EventEntityHeader/EventEntityHeader.styled';
import { Summary } from '../common/Summary';
import {
  ModalBody,
  ModalBodyDataContainer,
  ModalBodyHeaderContainer,
  ModalFooter,
} from '../Modal/Modal.styled';
import { RetailSaleBuyerStep } from './components/RetailSaleBuyerStep';
import { RetailSaleCheckoutStep } from './components/RetailSaleCheckoutStep';
import * as styles from './RetailSale.css';
import {
  RetailSaleLocalContextProvider,
  useRetailSaleContext,
} from './RetailSaleLocalContext';
import { RetailSaleFormValues } from './types';

export type RetailSaleStep = 'buyer' | 'checkout';

export const RetailSale = () => {
  const { posEntity, posEntityDisplayId, posEntityId } =
    useActivePosEntityContext<ListingDetails>();

  const listing = useMemo(() => {
    return posEntity?.entityType === ActionOutboxEntityType.Listing
      ? (posEntity as ListingDetails)
      : undefined;
  }, [posEntity]);

  const { isLoading: entityIsLoading, loadingState } =
    useEventItemLoadingDisplay<ListingDetails>(
      FormatContentId.LoadingListingId,
      FormatContentId.SearchingForListingId,
      FormatContentId.CouldNotFindListingId,
      posEntityDisplayId ?? posEntityId?.toString()
    );

  const methods = useForm<RetailSaleFormValues>({
    defaultValues: {},
  });

  const eventsTransformed = useCatalogDataContextEventsTransformedSelector();
  const event = useMemo(() => {
    return eventsTransformed?.find(
      (e) => e.event?.virtId === listing?.viagVirtId
    );
  }, [eventsTransformed, listing?.viagVirtId]);

  useEffect(() => {
    if (listing) {
      methods.reset({
        externalSaleId: `Retail-${new Date().getTime()}`,
        listingId: listing.id ?? 0,
        currencyCode: listing.currency ?? 'USD',
        saleDate: new Date().toISOString(),
        useShippingAddressOnFile: false,
        billingAddressSameAsShipping: true,
        tags: listing.tags,
        quantitySold: listing.availQty,
        section: listing.seating.section,
        row: listing.seating.row,
        seatFrom: listing.seating.seatFr,
        seatTo: listing.seating.seatTo,
        marketplacePaymentLines: [],
      });
    }
  }, [listing, methods]);

  if (!listing && entityIsLoading) {
    return loadingState;
  }

  return (
    <FormProvider {...methods}>
      <RetailSaleLocalContextProvider event={event} listing={listing}>
        <RetailSaleContent event={event} listing={listing} />
      </RetailSaleLocalContextProvider>
    </FormProvider>
  );
};

const RetailSaleContent = ({
  event,
  listing,
}: {
  event?: EventWithData;
  listing?: ListingDetails;
}) => {
  const [step, setStep] = useState<RetailSaleStep>('buyer');
  const {
    selectedBuyer,
    disabled,
    onUpsertBuyer,
    onCompleteAsUnpaid,
    onPayAndComplete,
  } = useRetailSaleContext();
  const { watch } = useFormContext<RetailSaleFormValues>();
  const input = watch();

  const isValidBuyer = useMemo(() => {
    if (
      selectedBuyer == null ||
      selectedBuyer.shippingAddressLine1 == null ||
      selectedBuyer.billingAddressLine1 == null
    ) {
      return (
        !!input.buyerName &&
        !!input.buyerEmail &&
        !!input.buyerPhone &&
        !!input.shippingStreet &&
        !!input.shippingState &&
        !!input.shippingCountry &&
        !!input.shippingZipCode &&
        (input.billingAddressSameAsShipping ||
          (!!input.billingStreet &&
            !!input.billingState &&
            !!input.billingCountry &&
            !!input.billingZipCode))
      );
    }
    return true;
  }, [
    input.billingAddressSameAsShipping,
    input.billingCountry,
    input.billingState,
    input.billingStreet,
    input.billingZipCode,
    input.buyerEmail,
    input.buyerName,
    input.buyerPhone,
    input.shippingCountry,
    input.shippingState,
    input.shippingStreet,
    input.shippingZipCode,
    selectedBuyer,
  ]);

  const canContinueToPayment = useMemo(
    () =>
      isValidBuyer &&
      !!input.saleDate &&
      input.totalNetProceeds != null &&
      input.totalNetProceeds > 0 &&
      !!input.externalSaleId &&
      !!input.ticketType &&
      input.quantitySold != null &&
      input.quantitySold > 0,
    [
      input.externalSaleId,
      input.quantitySold,
      input.saleDate,
      input.ticketType,
      input.totalNetProceeds,
      isValidBuyer,
    ]
  );

  const { closeModal } = useContext(ModalContext);

  const goToCheckout = useCallback(async () => {
    const ok = await onUpsertBuyer();
    if (ok) setStep('checkout');
  }, [onUpsertBuyer]);
  const onCancel = useCallback(() => closeModal(true), [closeModal]);

  return (
    <>
      <CancellableFormHeader onClose={onCancel}>
        <HeaderContent>
          <Stack direction="row" gap="m" alignItems="center">
            <span className={styles.breadcrumbHeader}>
              <Content id={ContentId.RetailSale} />
            </span>
            <span className={styles.breadcrumbSeparator}>&gt;</span>
            <span className={styles.breadcrumbItem}>
              <Content id={ContentId.Checkout} />
            </span>
          </Stack>
        </HeaderContent>
      </CancellableFormHeader>

      <ModalBody>
        {event?.event && (
          <ModalBodyHeaderContainer>
            <Summary
              event={event.event}
              posEntity={listing}
              entityType={ActionOutboxEntityType.Listing}
            />
          </ModalBodyHeaderContainer>
        )}

        <ModalBodyDataContainer>
          {step === 'buyer' ? (
            <RetailSaleBuyerStep listing={listing} />
          ) : (
            <RetailSaleCheckoutStep />
          )}
        </ModalBodyDataContainer>
      </ModalBody>

      <ModalFooter>
        {step === 'buyer' ? (
          <Stack direction="row" gap="m" justifyContent="end" width="full">
            <CancelButton type="button" onClick={onCancel} />
            <Button
              type="button"
              onClick={goToCheckout}
              disabled={!canContinueToPayment}
            >
              <Content id={ContentId.ContinueToPayment} />
            </Button>
          </Stack>
        ) : step === 'checkout' ? (
          <Stack direction="row" gap="m" justifyContent="end" width="full">
            <CancelButton
              type="button"
              disabled={disabled}
              onClick={onCancel}
            />
            <Button
              type="button"
              onClick={onCompleteAsUnpaid}
              disabled={disabled}
              variant="outline"
            >
              <Content id={ContentId.CompleteAsUnpaid} />
            </Button>
            <Button
              type="button"
              onClick={onPayAndComplete}
              disabled={disabled}
            >
              <Content id={ContentId.PayAndComplete} />
            </Button>
          </Stack>
        ) : null}
      </ModalFooter>
    </>
  );
};
